import { createSelector } from 'reselect';

import { selectGetConfigBoolValue, selectGetConfigValue } from '@pressreader/config';
import { error } from '@pressreader/logger';
import { TargetPlatform } from '@pressreader/types';

import { EXPERIMENTAL_FEATURES_FEATURE_FLAG, MODULE_NAME } from '../../constants';
import { AppGroupType, VALID_APP_GROUP_TYPES } from '../../types/app.group.type';
import { PervasiveCatalogLayout } from '../../types/pervasive.catalog.layout';
import { validateTargetPlatform } from '../../utils';
import { State } from '../reducer';

const getState = (state: { [MODULE_NAME]: State }) => state[MODULE_NAME];

export const selectDefaultTargetPlatform = createSelector(selectGetConfigValue, getConfigValue =>
    getConfigValue<TargetPlatform>('features.app.target.platform', TargetPlatform.Web),
);

export const selectTargetPlatform = createSelector(getState, state => state.targetPlatform);

/** True when the app runs in the Pervasive (accessibility) platform. */
export const selectIsPervasive = createSelector(selectTargetPlatform, platform => platform === TargetPlatform.Pervasive);

export const selectSupportedPlatforms = createSelector(selectTargetPlatform, selectGetConfigValue, (targetPlatform, getConfigValue) => {
    const supportedPlatformsJoined = getConfigValue<string>('features.app.platforms.supported', '') || '';

    const supportedPlatforms = supportedPlatformsJoined.split(',').filter(validateTargetPlatform);

    if (supportedPlatforms.indexOf(targetPlatform) < 0) {
        supportedPlatforms.push(targetPlatform);
    }

    return supportedPlatforms;
});

export const selectFaceliftAllowed = createSelector(selectGetConfigValue, getConfigValue =>
    getConfigValue<boolean>('features.app.facelift.allowed', false),
);
export const selectFaceliftEnabled = createSelector(getState, state => state.faceliftEnabled);

export const selectIsWebSDK = createSelector(getState, state => state.isWebSDK);

export const selectWebSDKMode = createSelector(getState, state => state.webSDKMode);

export const selectExperimentalEnabled = createSelector(selectGetConfigBoolValue, getConfigBoolValue =>
    getConfigBoolValue(EXPERIMENTAL_FEATURES_FEATURE_FLAG),
);

export const selectGiftCardTopbarEnabled = createSelector(
    selectTargetPlatform,
    selectGetConfigValue,
    (platform, getConfigValue) =>
        [TargetPlatform.Pervasive, TargetPlatform.Voyager].indexOf(platform) < 0 &&
        getConfigValue<boolean>('features.app.topbar.giftcard.enabled', false),
);

export const selectNewPurchaseFlowEnabled = createSelector(selectGetConfigValue, getConfigValue =>
    getConfigValue<boolean>('features.app.purchase.flow.enabled', false),
);

export const selectPervasiveFooterEnabled = createSelector(selectGetConfigValue, getConfigValue =>
    getConfigValue<boolean>('features.pervasive.footer.enabled', true),
);

// E+ storefront header redesign (#173983): centered brand logo + centered nav row on desktop.
// Default off — PressReader pervasive keeps the legacy layout; BE backend opts in per host.
export const selectPervasiveStorefrontHeaderEnabled = createSelector(
    selectTargetPlatform,
    selectGetConfigValue,
    (platform, getConfigValue) =>
        platform === TargetPlatform.Pervasive && getConfigValue<boolean>('features.pervasive.storefront.header.enabled', false),
);

export const selectPervasiveHomeWelcomeEnabled = createSelector(selectGetConfigValue, getConfigValue =>
    getConfigValue<boolean>('features.pervasive.home.welcome.enabled', true),
);

export const selectPervasiveCatalogLayout = createSelector(selectGetConfigValue, getConfigValue =>
    getConfigValue<PervasiveCatalogLayout>('features.pervasive.catalog.layout', PervasiveCatalogLayout.Carousel),
);

export const selectAppGroupType = createSelector(selectGetConfigValue, getConfigValue => {
    const appGroupType = getConfigValue<AppGroupType>('features.app.group.type', AppGroupType.Pressreader);

    if (VALID_APP_GROUP_TYPES.indexOf(appGroupType) < 0) {
        error(`Invalid app group type ${appGroupType}`);
    }
    return appGroupType;
});

export const selectLegacyPlatform = createSelector(getState, state => state.legacyPlatform);

export const selectAppStarted = createSelector(getState, state => state.appStarted);

export const selectIsOnline = createSelector(getState, state => state.isOnline);
